import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useContenido } from "@/context/ContenidoContext";
import { toast } from "sonner";

export default function CrmContenido() {
    const { refresh: refreshGlobal } = useContenido();
    const [config, setConfig] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/contenido/config")
            .then((r) => setConfig(r.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, []);

    if (!config) return <p className="text-piedra italic font-body">Cargando...</p>;

    const update = (k, v) => setConfig({ ...config, [k]: v });
    const updateHorario = (idx, k, v) => {
        const horarios = [...config.horarios];
        horarios[idx] = { ...horarios[idx], [k]: v };
        setConfig({ ...config, horarios });
    };
    const updateChuleton = (k, v) =>
        setConfig({ ...config, chuleton: { ...config.chuleton, [k]: v } });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                telefono: config.telefono,
                telefono_link: config.telefono_link,
                email: config.email,
                whatsapp: config.whatsapp,
                facebook_url: config.facebook_url,
                direccion: config.direccion,
                google_maps_query: config.google_maps_query,
                horarios: config.horarios,
                chuleton: config.chuleton,
                pan_precio: config.pan_precio,
                aforo_comida: config.aforo_comida,
                aforo_cena: config.aforo_cena,
                aforo_franja: config.aforo_franja,
            };
            await api.patch("/contenido/config", payload);
            toast.success("Cambios guardados");
            refreshGlobal();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-12">
            <header>
                <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                    Contenido · Datos generales
                </p>
                <h1 className="font-heading text-5xl text-crema font-light">
                    Horarios y <em className="italic text-dorado">contacto</em>
                </h1>
            </header>

            <section className="border border-dorado/20 bg-carbon-suave/40 p-8">
                <h2 className="font-heading text-2xl text-crema mb-6">Contacto</h2>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div>
                        <label className="line-label">Teléfono (mostrado)</label>
                        <input
                            value={config.telefono || ""}
                            onChange={(e) => update("telefono", e.target.value)}
                            className="input-line"
                            data-testid="config-telefono"
                        />
                    </div>
                    <div>
                        <label className="line-label">Teléfono (formato tel:)</label>
                        <input
                            value={config.telefono_link || ""}
                            onChange={(e) => update("telefono_link", e.target.value)}
                            className="input-line"
                            placeholder="+34925484633"
                        />
                    </div>
                    <div>
                        <label className="line-label">Email</label>
                        <input
                            type="email"
                            value={config.email || ""}
                            onChange={(e) => update("email", e.target.value)}
                            className="input-line"
                        />
                    </div>
                    <div>
                        <label className="line-label">WhatsApp (con prefijo)</label>
                        <input
                            value={config.whatsapp || ""}
                            onChange={(e) => update("whatsapp", e.target.value)}
                            className="input-line"
                            placeholder="+34925484633"
                            data-testid="config-whatsapp"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="line-label">Facebook URL</label>
                        <input
                            value={config.facebook_url || ""}
                            onChange={(e) => update("facebook_url", e.target.value)}
                            className="input-line"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="line-label">Dirección</label>
                        <input
                            value={config.direccion || ""}
                            onChange={(e) => update("direccion", e.target.value)}
                            className="input-line"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="line-label">
                            Búsqueda Google Maps (para el embed)
                        </label>
                        <input
                            value={config.google_maps_query || ""}
                            onChange={(e) => update("google_maps_query", e.target.value)}
                            className="input-line"
                        />
                    </div>
                </div>
            </section>

            <section className="border border-dorado/20 bg-carbon-suave/40 p-8">
                <h2 className="font-heading text-2xl text-crema mb-6">Horario</h2>
                <div className="space-y-4">
                    {config.horarios.map((h, idx) => (
                        <div
                            key={h.dia}
                            className="grid grid-cols-12 gap-4 items-center border-b border-dorado/10 pb-3"
                            data-testid="config-horario-row"
                        >
                            <div className="col-span-3 font-heading text-crema">{h.dia}</div>
                            <div className="col-span-7">
                                <input
                                    value={h.horas}
                                    onChange={(e) => updateHorario(idx, "horas", e.target.value)}
                                    className="input-line"
                                    placeholder="11:00 – 16:30"
                                />
                            </div>
                            <div className="col-span-2 flex items-center justify-end gap-2">
                                <label className="flex items-center gap-2 font-body text-xs text-piedra cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!h.cerrado}
                                        onChange={(e) =>
                                            updateHorario(idx, "cerrado", e.target.checked)
                                        }
                                        className="accent-dorado"
                                    />
                                    Cerrado
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="border border-dorado/20 bg-carbon-suave/40 p-8">
                <h2 className="font-heading text-2xl text-crema mb-6">
                    Especialidad · Chuletón
                </h2>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div>
                        <label className="line-label">Precio</label>
                        <input
                            value={config.chuleton?.precio || ""}
                            onChange={(e) => updateChuleton("precio", e.target.value)}
                            className="input-line"
                            placeholder="56€/kg"
                        />
                    </div>
                    <div>
                        <label className="line-label">Pan (precio)</label>
                        <input
                            value={config.pan_precio || ""}
                            onChange={(e) => update("pan_precio", e.target.value)}
                            className="input-line"
                            placeholder="1,5"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="line-label">Descripción del chuletón</label>
                        <input
                            value={config.chuleton?.descripcion || ""}
                            onChange={(e) => updateChuleton("descripcion", e.target.value)}
                            className="input-line"
                        />
                    </div>
                </div>
            </section>

            {/* ----- Aforo / Anti-overbooking ----- */}
            <section className="space-y-6">
                <div>
                    <h2 className="font-heading text-3xl text-crema font-light">
                        Aforo y <em className="italic text-dorado">overbooking</em>
                    </h2>
                    <p className="font-body text-sm text-piedra mt-2 max-w-2xl">
                        Estos límites bloquean automáticamente nuevas reservas online cuando se completan, evitando overbooking. La validación es por franja (cocina) y por servicio (sala). Si lo dejas vacío se usan los valores por defecto (40 / 30 / 14).
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <label className="line-label">Aforo servicio comida (sala total)</label>
                        <input
                            type="number"
                            min="1"
                            value={config.aforo_comida ?? ""}
                            onChange={(e) =>
                                update("aforo_comida", e.target.value ? Number(e.target.value) : null)
                            }
                            className="input-line"
                            placeholder="40"
                            data-testid="aforo-comida"
                        />
                    </div>
                    <div>
                        <label className="line-label">Aforo servicio cena (sala total)</label>
                        <input
                            type="number"
                            min="1"
                            value={config.aforo_cena ?? ""}
                            onChange={(e) =>
                                update("aforo_cena", e.target.value ? Number(e.target.value) : null)
                            }
                            className="input-line"
                            placeholder="30"
                            data-testid="aforo-cena"
                        />
                    </div>
                    <div>
                        <label className="line-label">Aforo por franja (cocina)</label>
                        <input
                            type="number"
                            min="1"
                            value={config.aforo_franja ?? ""}
                            onChange={(e) =>
                                update("aforo_franja", e.target.value ? Number(e.target.value) : null)
                            }
                            className="input-line"
                            placeholder="14"
                            data-testid="aforo-franja"
                        />
                    </div>
                </div>
            </section>

            <div className="text-right">
                <button
                    onClick={save}
                    disabled={saving}
                    className="btn-gold-solid disabled:opacity-50"
                    data-testid="config-save"
                >
                    {saving ? "Guardando..." : "Guardar cambios"}
                </button>
            </div>
        </div>
    );
}
